import type { Course } from "@/types/course";
import type { SortKey } from "@/utils/courseSort";
import { getTypeLabel } from "@/utils/courseType";
import { getSeedChipClass } from "@/utils/courseSeed";
import { getSatisfactionBadge } from "@/utils/courseSatisfaction";

interface CourseCardProps {
  course: Course;
  checked: boolean;
  disabled: boolean;
  sortKey: SortKey;
  onToggle: (id: string) => void;
}

interface StatValueProps {
  label: string;
  value: string;
  highlight: boolean;
}

function StatValue({ label, value, highlight }: StatValueProps) {
  return (
    <span className="flex flex-col items-start">
      <span className="text-xs leading-none text-gray-600 dark:text-gray-400">{label}</span>
      <span
        className={`mt-1 text-xs leading-none whitespace-nowrap ${
          highlight ? "font-bold text-brand-700 dark:text-brand-400" : "text-gray-700 dark:text-gray-300"
        }`}
      >
        {value}
      </span>
    </span>
  );
}

export default function CourseCard({
  course,
  checked,
  disabled,
  sortKey,
  onToggle,
}: CourseCardProps) {
  const satisfactionBadge = getSatisfactionBadge(course.satisfaction);

  return (
    <button
      type="button"
      onClick={() => onToggle(course.id)}
      disabled={disabled}
      aria-pressed={checked}
      className={`flex h-full w-full flex-col rounded-lg border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-brand-400 dark:focus-visible:ring-offset-gray-900 ${
        checked
          ? "border-brand-600 bg-brand-50 dark:border-brand-500 dark:bg-brand-900/30"
          : disabled
            ? "cursor-not-allowed border-gray-200 bg-gray-50 opacity-50 dark:border-gray-700 dark:bg-gray-800"
            : "border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600"
      }`}
    >
      <span className="flex items-start gap-3">
        <span
          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
            checked
              ? "border-brand-600 bg-brand-600 dark:border-brand-500 dark:bg-brand-500"
              : "border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900"
          }`}
        >
          {checked && <i className="ri-check-line text-sm text-white" />}
        </span>

        <span className="min-w-0 flex-1">
          <span className="mb-1.5 flex flex-wrap items-center gap-1.5">
            <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${getSeedChipClass(course.seed)}`}>
              {course.seed}
            </span>
            <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${satisfactionBadge.className}`}>
              {satisfactionBadge.label}
            </span>
          </span>
          <span className="block text-sm font-semibold leading-snug text-gray-900 break-keep dark:text-gray-100">
            {course.name}
          </span>
          <span className="mt-1 block text-xs text-gray-500 break-keep dark:text-gray-400">
            {course.institution}
          </span>
          <span className="mt-0.5 block text-xs text-gray-600 break-keep dark:text-gray-400">
            {course.region} · {getTypeLabel(course.typeCode)}
          </span>
        </span>
      </span>

      <span className="mt-auto flex items-end justify-between gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
        <StatValue
          label="수강비"
          value={`${course.tuition.toLocaleString("ko-KR")}원`}
          highlight={sortKey === "tuition"}
        />
        <StatValue
          label="만족도"
          value={`${course.satisfaction}%`}
          highlight={sortKey === "satisfaction"}
        />
        <StatValue
          label="취업률 3개월"
          value={course.employment3m}
          highlight={sortKey === "employment3m"}
        />
      </span>
    </button>
  );
}